import {v4 as uuidv4} from 'uuid';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';

export {downloadFile, getFileNameFromURL} from './downloadFile';
export {
    normalizeSolutions,
    applyStageFilter,
    buildStageData,
    buildEvolveStageParams,
    generateUniqueIslandList
} from './solutionUtils';
export {downloadReportAsPDF} from './downloadReportAsPDF';

export function downloadByLink(url?: string, filename?: string) {
    if (!url) {
        return;
    }

    const link = document.createElement('a');
    link.href = url;
    link.download = filename || '';
    link.rel = 'noopener noreferrer';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    window.setTimeout(() => {
        document.body.removeChild(link);
    }, 0);
}

export function downloadByLinks(urls: string[]) {
    urls.filter(Boolean).forEach((url, index) => {
        window.setTimeout(() => {
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = url;
            document.body.appendChild(iframe);
            window.setTimeout(() => {
                document.body.removeChild(iframe);
            }, 5000);
        }, index * 500);
    });
}

dayjs.extend(customParseFormat);
dayjs.extend(utc);

interface RawNode {
    name: string;
    is_dir: boolean;
    path: string;
    bos_url?: string;
    children?: RawNode[];
}

interface RawData {
    root: RawNode[];
}
function transformNode(node: RawNode, star_data: FileInfo): FileInfo {
    return {
        id: uuidv4(),
        star: star_data?.path === node.path,
        ...node,
        children: [
            ...(node.children ? node.children.map(child => transformNode(child, star_data)) : [])
        ]
    };
}

export function transformToAlgorithmTree(data: RawData, star_data: FileInfo): FileInfo[] {
    return data.root.map(item => transformNode(item, star_data));
}

export function transformToEvaluatorList(data: FileInfo[]) {
    return data.map(item => ({
        ...item,
        id: uuidv4(),
        name: item.file_name || item.name
    }));
}


export function nameValidReg(text: string) {
    return /^[a-zA-Z0-9_]+\.py$/.test(text);
}

// export const getFileIcon = (filename: string): string => {
//     const ext = filename?.split('.').pop()?.toLowerCase();
//     switch (ext) {
//         case 'py':
//             return 'codicon-python c-519aba'; // Python 用代码图标
//         case 'csv':
//             return 'codicon-table c-8dc149'; // CSV 用表格图标
//         case 'tar':
//         case 'zip':
//         case 'gz':
//         case 'rar':
//             return 'codicon-file-zip c-6d8086'; // 压缩包
//         case 'json':
//             return 'codicon-json c-519aba';
//         case 'md':
//             return 'codicon-markdown c-519aba';
//         case 'txt':
//             return 'codicon-symbol-parameter c-d4d7d6';
//         case 'yml':
//         case 'yaml':
//             return 'codicon-code c-8dc149';
//         default:
//             return 'codicon-file-code'; // 默认文件
//     }
// };

// 文件图标配置映射
export const fileIconConfig = {
    // 压缩文件
    zip: {icon: 'mdi:folder-zip', color: '#FF9800'},
    rar: {icon: 'mdi:folder-zip-outline', color: '#F44336'},
    tar: {icon: 'mdi:archive', color: '#795548'},
    gz: {icon: 'mdi:archive', color: '#607D8B'},
    // JavaScript 相关文件
    js: {icon: 'mdi:language-javascript', color: '#f7df1e'}, // JavaScript 黄色
    jsx: {icon: 'mdi:react', color: '#61dafb'}, // React 蓝色
    mjs: {icon: 'mdi:language-javascript', color: '#f7df1e'}, // ES Module JavaScript
    cjs: {icon: 'mdi:language-javascript', color: '#f7df1e'}, // CommonJS JavaScript
    // 文本文件
    txt: {icon: 'mdi:file-document-outline', color: '#78909C'},
    log: {icon: 'mdi:file-document-outline', color: '#546E7A'},

    // TypeScript 相关文件
    ts: {icon: 'mdi:language-typescript', color: '#3178c6'}, // TypeScript 蓝色
    tsx: {icon: 'mdi:react', color: '#61dafb'}, // React with TypeScript

    // PDF 文件
    pdf: {icon: 'mdi:file-pdf-box', color: '#f40f02'}, // PDF 红色
    // CSV文件
    csv: {icon: 'mdi:table-large', color: '#1099e3'},
    py: {icon: 'mdi:language-python', color: '#0fabff'}, // Python 蓝色
    java: {icon: 'mdi:language-java', color: '#ed8b00'}, // Java 橙色
    // 配置文件
    json: {icon: 'codicon:json', color: '#FFD600'},
    yml: {icon: 'mdi:file-code-outline', color: '#9C27B0'},
    yaml: {icon: 'mdi:file-code-outline', color: '#9C27B0'},
    xml: {icon: 'mdi:xml', color: '#FF6D00'},
    toml: {icon: 'mdi:file-cog-outline', color: '#42A5F5'},
    ini: {icon: 'mdi:cog', color: '#78909C'},
    conf: {icon: 'mdi:file-cog', color: '#78909C'},
    cfg: {icon: 'mdi:file-cog', color: '#78909C'},
    env: {icon: 'mdi:key', color: '#FF7043'},
    // 默认文件
    default: {icon: 'codicon:file', color: '#CCCCCC'}
};


export function formatBackendTime(value?: string): string {
    if (!value) {
        return '-';
    }
    const d = new Date(value);
    if (isNaN(d.getTime())) {
        return value;
    }
    return d.toLocaleString('zh-CN', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false
    });
}

export function getProgressPercentage(progress: number, defaultValue = 0) {
    const num = typeof progress === 'string' ? parseFloat(progress) : progress;
    return isNaN(num) ? defaultValue : Math.round(num * 100);
}


/**
 * 将秒数转换为天、小时、分钟和秒的格式，避免浮点数精度问题
 * @param {number} seconds - 要转换的秒数
 * @param {number} decimalPlaces - 秒数小数位数（默认：0，即只显示整数秒）
 * @returns {string} 格式化后的时间字符串，例如："2天3小时15分钟30秒"
 */
export function formatSecondsToHMS(seconds: number, decimalPlaces = 0) {
    if (isNaN(seconds) || seconds < 0) {
        return 0;
    }

    // 处理浮点数精度问题
    const preciseSeconds = Number(seconds.toFixed(10));

    // 计算天、小时、分钟和秒
    const days = Math.floor(preciseSeconds / 86400); // 1天 = 24 * 60 * 60 = 86400秒
    const hours = Math.floor((preciseSeconds % 86400) / 3600);
    const minutes = Math.floor((preciseSeconds % 3600) / 60);
    let remainingSeconds = preciseSeconds % 60;

    // 处理秒数的小数部分
    if (decimalPlaces > 0) {
        remainingSeconds = Number(remainingSeconds.toFixed(decimalPlaces));
    } else {
        // 默认情况下，四舍五入到整数
        remainingSeconds = Math.round(remainingSeconds);

        // 如果四舍五入后秒数为60，进位到分钟
        if (remainingSeconds === 60) {
            remainingSeconds = 0;
            // 注意：这里需要处理分钟进位，但通常不会出现这种情况
        }
    }

    // 构建结果字符串
    let result = '';

    if (days > 0) {
        result += `${days}天`;
    }

    if (hours > 0) {
        result += `${hours}小时`;
    }

    if (minutes > 0) {
        result += `${minutes}分`;
    }

    if (remainingSeconds > 0 || result === '') {
        result += `${remainingSeconds}秒`;
    }

    return result;
}
/**
   * 科学计数法格式化
   * @param {number} num - 数字
   * @param {number} decimalPlaces - 小数位数
   * @returns {string} 科学计数法字符串
   */
function formatScientific(num, decimalPlaces = 2) {
    const absNum = Math.abs(num);
    const exponent = Math.floor(Math.log10(absNum));
    const coefficient = num / Math.pow(10, exponent);

    // 格式化系数，保留指定小数位数
    const formattedCoefficient = coefficient.toFixed(decimalPlaces);

    return `${formattedCoefficient}e^${exponent}`;
}

/**
   * 常规数字格式化
   * @param {number} num - 数字
   * @param {number} maxDigits - 最大位数
   * @returns {string} 格式化后的字符串
   */
function formatRegularNumber(num, maxDigits) {
    const numStr = num.toString();

    // 如果是整数
    if (Number.isInteger(num)) {
        const integerStr = numStr;
        if (integerStr.length <= maxDigits) {
            return integerStr;
        } else {
            return formatScientific(num, 2);
        }
    }

    // 小数处理
    const [integerPart, decimalPart] = numStr.split('.');

    if (integerPart.length >= maxDigits) {
        return formatScientific(num, 2);
    }

    const availableDecimalDigits = maxDigits - integerPart.length;
    const truncatedDecimal = decimalPart.substring(0, Math.floor(availableDecimalDigits));

    return `${integerPart}.${truncatedDecimal}`;
}

/**
   * 获取数字的小数位数
   * @param {number} num - 数字
   * @returns {number} 小数位数
   */
function getDecimalDigits(num) {
    const numStr = num.toString();
    if (!numStr.includes('.')) {
        return 0;
    }

    const decimalPart = numStr.split('.')[1];
    return decimalPart.length;
}
/**
   * 分数显示格式化（显示6.5位数，超过使用科学计数法）
   * @param {number} num - 要格式化的数字
   * @returns {string} 格式化后的字符串
   */
export function formatFractionDisplay(num) {
    if (typeof num !== 'number' || isNaN(num)) {
        return '0';
    }

    if (num === 0) {
        return '0';
    }
    if (Math.abs(num) < 1e-6) {
        return '0';
    }

    const absNum = Math.abs(num);
    const integerPartLength = Math.floor(absNum).toString().length;

    // 判断是否需要科学计数法（整数部分超过6位或小数部分导致总字符超过6.5）
    if (integerPartLength > 6
        || (integerPartLength + getDecimalDigits(absNum)) > 6.5
        || (absNum > 0 && absNum < 0.001)) {
        return formatScientific(num, 2); // 保留2位小数
    }

    // 常规数字处理，考虑6.5位数的限制
    return formatRegularNumber(num, 6.5);
}

/**
 * 从 loginstate 中提取指定 key 的值
 * @param loginstate - 例如: "templateid_2222_invitecode_33333"
 * @param key - 要提取的 key，例如: "templateid"
 */
export function extractValue(loginstate: string | null, key: string): string | null {
    if (!loginstate) {
        return null;
    }

    // 动态构建正则：key_([^_]+)
    const regex = new RegExp(`${key}_([^_]+)`);
    const match = regex.exec(loginstate);
    return match ? match[1] : null;
}

/**
 * 获取用户名的首字符（大写）
 * @param userName - 用户名
 * @returns 首字符大写，如果用户名为空则返回空字符串
 */
export function getFirstChar(userName?: string): string {
    return userName ? userName.charAt(0).toUpperCase() : '';
}

/**
 * 判断字符是否为中文
 * @param char - 要判断的字符
 * @returns 是否为中文字符
 */
export function isChinese(char: string): boolean {
    return /[\u4e00-\u9fa5]/.test(char);
}

/**
 * 获取头像文字的字体大小（中文12px，英文14px）
 * @param userName - 用户名
 * @returns 字体大小字符串
 */
export function getAvatarFontSize(userName?: string): string {
    const firstChar = getFirstChar(userName);
    return isChinese(firstChar) ? '12px' : '14px';
}


/**
 * 将 params 对象转换为 URL 查询字符串
 * @param params - 参数对象，例如: { invitecode: '123', name: 'test' }
 * @returns 查询字符串，例如: "?invitecode=123&name=test" 或 ""
 */
export function buildQueryString(params?: Record<string, any>): string {
    if (!params) {
        return '';
    }

    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        // 只添加有值的参数（过滤 null、undefined、空字符串）
        if (value !== null && value !== undefined && value !== '') {
            searchParams.append(key, String(value));
        }
    });

    const queryString = searchParams.toString();
    return queryString ? `?${queryString}` : '';
}

interface SolutionTitlePayload {
    s_id?: string;
    generation?: number;
    iteration_found?: number | string;
}

/**
 * 生成解详情卡片标题
 * - generation === 0 且 s_id 以 init 开头时，展示“init#X”
 * - 其他情况展示“#iteration_found”
 */
export function getSolutionCardTitle(data: SolutionTitlePayload): string {
    const sid = data.s_id || '';
    const normalizedSid = sid.toLowerCase();

    if (data.generation === 0 && normalizedSid.startsWith('init')) {
        // 兼容 init_1_island_0 / init_island_0 等格式：
        // - 优先取 init 后第一个数字（忽略 island 段）
        // - init_island_0 这类未携带种子编号时默认 0
        const seedMatch = /^init(?:_(\d+))?(?:_island_\d+)?$/i.exec(sid);
        if (seedMatch?.[1]) {
            return `种子#${seedMatch[1]}`;
        }
        return '种子#0';
    }

    return `#${data.iteration_found ?? 0}`;
}

export function getSolutionCardParentTitle(data: SolutionTitlePayload): string {
    const sid = data.s_id || '';
    if (sid.toLowerCase().startsWith('init')) {
        // 兼容 init_1_island_0 / init_island_0 等格式：
        // - 优先取 init 后第一个数字（忽略 island 段）
        // - init_island_0 这类未携带种子编号时默认 0
        const seedMatch = /^init(?:_(\d+))?(?:_island_\d+)?$/i.exec(sid);
        if (seedMatch?.[1]) {
            return `种子#${seedMatch[1]}`;
        }
        return '种子#0';
    }

    return `#${data.iteration_found ?? 0}`;
}

export function getSubscriptionPlanText(subscriptionPlan?: string): string {
    if (!subscriptionPlan || subscriptionPlan === '-') {
        return '-';
    }

    if (subscriptionPlan === 'Enterprise') {
        return '企业版';
    }

    return subscriptionPlan.toLowerCase() === 'pro' ? '进阶版' : '免费版';
}

export function getSubscriptionPlanDesc(subscriptionPlan?: string, time?: string): string {
    if (!subscriptionPlan || subscriptionPlan === '-') {
        return '-';
    }

    if (subscriptionPlan === 'Enterprise') {
        return '-';
    }

    if (subscriptionPlan === 'Pro') {
        return `${time} 到期`;
    }

    return '升级订阅以获取更多权益';
}

/**
 * 数字千分位格式化（添加逗号分隔）
 * @param value - 要格式化的数字或字符串
 * @returns 格式化后的字符串，例如: "1,500,000"
 */
export function formatNumberWithCommas(value?: number | string): string {
    if (value === null || value === undefined || value === '') {
        return '-';
    }

    const num = typeof value === 'string' ? parseFloat(value) : value;

    if (isNaN(num)) {
        return String(value);
    }

    return num.toLocaleString('en-US');
}

/** DuMate 购买页跳转场景：credits=购买通用资源点 / renew=续费 / seat=扩充席位 */
export type DuMatePurchaseScene = 'credits' | 'renew' | 'seat';

/**
 * 获取 DuMate 购买页跳转地址，按环境区分域名（席位/资源点/续费同一套域名，与 fm-agent-fe 对齐）。
 * - 线上（生产构建 import.meta.env.MODE === 'production'）：https://console.bce.baidu.com
 * - 测试（开发 / 其他构建）：https://qasandbox.bcetest.baidu.com
 * 场景仅决定 query：credits=?sourceType=resource-point / renew=?sourceType=famou / seat=?sourceType=famou&famouScene=expand
 */
export function getDuMatePurchaseUrl(scene: DuMatePurchaseScene = 'credits'): string {
    const mode = (import.meta as {env?: {MODE?: string}}).env?.MODE;
    const origin = mode === 'production'
        ? 'https://console.bce.baidu.com'
        : 'https://qasandbox.bcetest.baidu.com';
    const base = `${origin}/dumate/purchase-page/new`;

    if (scene === 'renew') {
        return `${base}?sourceType=famou`;
    }

    if (scene === 'seat') {
        return `${base}?sourceType=famou&famouScene=expand`;
    }

    return `${base}?sourceType=resource-point`;
}
